import React, { useMemo, useState } from 'react';
import { Eye, EyeOff, Lock, TreePine } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { resetPassword } from '../services/api';

const ResetPassword = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const queryToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  }, [location.search]);

  const [token, setToken] = useState(queryToken);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(t('portalUi.login.tokenRequired', 'Ievadiet tokenu'));
      return;
    }
    if (password.length < 12) {
      toast.error(t('portalUi.login.passwordMin', 'Parolei jābūt vismaz 12 simbolu garai'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('portalUi.login.passwordMismatch', 'Paroles nesakrīt'));
      return;
    }

    try {
      setIsSubmitting(true);
      await resetPassword(token, password);
      toast.success(t('portalUi.login.resetSuccess', 'Parole atjaunota'));
      navigate('/portal/login');
    } catch (error) {
      toast.error(t('portalUi.login.resetError', 'Neizdevās atjaunot paroli'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f2eb] via-[#f0f5ef] to-[#e8f0ea] flex items-center justify-center p-6" data-testid="portal-reset-page">
      <Card className="w-full max-w-lg shadow-xl border-0 bg-white/90 backdrop-blur">
        <CardHeader className="text-center space-y-2">
          <div className="w-14 h-14 rounded-2xl bg-[#1F3D2B] text-white flex items-center justify-center mx-auto">
            <TreePine className="w-7 h-7" />
          </div>
          <CardTitle className="text-2xl font-semibold text-[#1F3D2B]" data-testid="portal-reset-title">
            {t('portalUi.login.resetTitle', 'Iestatīt jaunu paroli')}
          </CardTitle>
          <p className="text-sm text-[#2E2E2E]/60" data-testid="portal-reset-subtitle">
            {t('portalUi.login.resetSubtitle', 'Ievadiet tokenu un jauno paroli')}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="portal-reset-form">
            <div className="space-y-2">
              <Label htmlFor="reset-token">{t('portalUi.login.resetToken', 'Tokens')}</Label>
              <Input
                id="reset-token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder={t('portalUi.login.resetTokenPlaceholder', 'Ievadiet tokenu')}
                data-testid="portal-reset-token"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-password">{t('portalUi.login.newPassword', 'Jaunā parole')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2E2E2E]/40" />
                <Input
                  id="reset-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('portalUi.login.newPasswordPlaceholder', 'Ievadiet jauno paroli')}
                  className="pl-10 h-12"
                  data-testid="portal-reset-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2E2E2E]/60"
                  data-testid="portal-reset-toggle"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-confirm">{t('portalUi.login.confirmPassword', 'Apstiprināt paroli')}</Label>
              <Input
                id="reset-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('portalUi.login.confirmPasswordPlaceholder', 'Atkārtojiet paroli')}
                data-testid="portal-reset-confirm"
                required
              />
            </div>
            <Button type="submit" className="w-full h-12" disabled={isSubmitting} data-testid="portal-reset-submit">
              {isSubmitting ? t('portalUi.common.loading', 'Ielādē...') : t('portalUi.login.resetSubmit', 'Saglabāt paroli')}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => navigate('/portal/login')} data-testid="portal-reset-cancel">
              {t('portalUi.login.backToLogin', 'Atgriezties uz login')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
